import { LegacyDocumentType } from '@prisma/client';
import { fetcher } from '../logical-backend-fetcher';

export type SignedUrlForPutResponse = {
  // The URL associated with this file to save in the database for future GET requests.
  hostedUrl: string;
  // URL to POST to to push this file to S3.
  signedUrl: string;
  // URL to GET from to read this file. Expires in 24 hours.
  // Required because otherwise you can't download a file until it's stored in our file system.
  // signedGetRequest: string;
};

type SignedUrlForPutRequest = {
  fileName: string;
  companyId?: number;
};

export const getSignedUrlForPut = async (
  sanitizedFileName: string,
  companyId: number | undefined,
  nextAuthToken?: string
): Promise<SignedUrlForPutResponse | null> => {
  try {
    const req: SignedUrlForPutRequest = {
      fileName: sanitizedFileName,
    };
    if (companyId) {
      req.companyId = companyId;
    }

    const res = await fetcher(
      `s3/signedUrlForPut`,
      {
        method: 'POST',
        body: req,
      },
      nextAuthToken || undefined
    );

    if (!res.ok) {
      console.error(await res.json());
      return null;
    }

    const data = await res.json();

    return data;
  } catch (err) {
    console.error(err);
    return null;
  }
};

export const getSignedS3PutUrl = async (
  fileName: string,
  type: string,
  companyId: number | undefined,
  token?: string
) => {
  const sanitizedFileName = fileName.replaceAll(/[^a-zA-Z0-9!\-_.*'()]/g, '_');

  const res = await getSignedUrlForPut(sanitizedFileName, companyId, token);

  if (!res) {
    console.error('Failed to get signed url ');
    return null;
  }

  return {
    signedUrl: res.signedUrl as string,
    url: res.hostedUrl as string,
  };
};

export const pushFileToS3Url = (signedRequest: string, file: File) => {
  const myHeaders = new Headers({
    'Content-Type': file.type || 'application/octet-stream',
  });
  return fetch(signedRequest, {
    method: 'PUT',
    headers: myHeaders,
    body: file,
  });
};

export type UploadFileResult =
  | { outcome: 'uploaded'; fileName: string; fileUrl: string }
  | {
      outcome: 'failed';
      stage: 'presign' | 'put';
      status?: number;
      reason: string;
    };

export async function uploadFile(
  file: File,
  companyId: number | undefined,
  token?: string
): Promise<UploadFileResult> {
  if (!file) {
    return {
      outcome: 'failed',
      stage: 'presign',
      reason: 'No file selected.',
    };
  }
  const urlResponse = await getSignedS3PutUrl(
    file.name,
    LegacyDocumentType.Unknown,
    companyId,
    token
  );
  if (!urlResponse) {
    return {
      outcome: 'failed',
      stage: 'presign',
      reason: 'Could not prepare the upload.',
    };
  }
  const { url, signedUrl } = urlResponse;

  try {
    const saveFileResult = await pushFileToS3Url(signedUrl, file);

    if (!saveFileResult.ok) {
      console.error(
        `Failed to save file to S3 URL (${saveFileResult.status})`,
        file.name
      );
      return {
        outcome: 'failed',
        stage: 'put',
        status: saveFileResult.status,
        reason: `Storage rejected the file (${saveFileResult.status}).`,
      };
    }

    return { outcome: 'uploaded', fileName: file.name, fileUrl: url };
  } catch (error) {
    console.error('Failed to reach file storage', file.name, error);
    return {
      outcome: 'failed',
      stage: 'put',
      reason: 'Could not reach file storage.',
    };
  }
}

export const getSignedUrlByFileId = async (
  fileId: number,
  token: string | undefined
): Promise<string> => {
  const endpoint = `s3/signedUrlForGet?${new URLSearchParams({
    fileId: String(fileId),
  })}`;
  try {
    const signedUrlRes = await fetcher(
      endpoint,
      {
        method: 'GET',
      },
      token
    );

    if (signedUrlRes.ok) {
      const url = await signedUrlRes.text();

      return url;
    }
    const bodyText = await signedUrlRes.text().catch(() => '');
    console.error(
      `Failed to get signed url for fileId=${fileId} (${signedUrlRes.status})`,
      bodyText
    );
    return null;
  } catch (err) {
    console.error(err);
    return null;
  }
};
